import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import '../@style/tradingview.css';

const TradingView = ({ selectedSymbol }) => {
  const { isDarkMode } = useTheme();
  const chartContainerRef = useRef(null);
  const [activeTimeframe, setActiveTimeframe] = useState('D');
  const [isMobile, setIsMobile] = useState(false);

  const defaultSymbol = selectedSymbol || 'GAUTRY';

  const getChartHeight = () => {
    if (typeof window === 'undefined') return 560;
    if (window.innerWidth <= 480) {
      return Math.max(360, Math.round(window.innerHeight * 0.45));
    }
    if (window.innerWidth <= 768) {
      return Math.max(400, Math.round(window.innerHeight * 0.5));
    }
    return Math.max(520, Math.min(720, Math.round(window.innerHeight * 0.58)));
  };

  const [chartHeight, setChartHeight] = useState(getChartHeight);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      setChartHeight(getChartHeight());
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (defaultSymbol && chartContainerRef.current) {
      const container = chartContainerRef.current;
      container.innerHTML = '';
      container.style.height = `${chartHeight}px`;
      container.style.minHeight = `${chartHeight}px`;

      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => {
        if (window.TradingView && chartContainerRef.current) {
          try {
            new window.TradingView.widget({
              symbol: defaultSymbol,
              interval: activeTimeframe,
              timezone: 'Europe/Istanbul',
              theme: isDarkMode ? 'dark' : 'light',
              style: '1',
              locale: 'tr',
              toolbar_bg: '#f1f3f6',
              enable_publishing: false,
              allow_symbol_change: true,
              container_id: chartContainerRef.current.id,
              width: '100%',
              height: chartHeight,
              studies: ['Volume@tv-basicstudies'],
              show_popup_button: true,
              popup_width: isMobile ? '100%' : '1000',
              popup_height: isMobile ? '100%' : '650',
              hide_side_toolbar: isMobile,
              hide_legend: isMobile,
              save_image: false,
              enable_touch: true,
              library_path: '/charting_library/',
              custom_css_url: '',
            });
          } catch (error) {
            console.error('TradingView widget oluşturma hatası:', error);
          }
        }
      };
      document.head.appendChild(script);

      return () => {
        if (chartContainerRef.current) {
          chartContainerRef.current.innerHTML = '';
        }
      };
    }
  }, [defaultSymbol, isDarkMode, activeTimeframe, isMobile, chartHeight]);

  const handleTimeframeChange = (timeframe) => {
    setActiveTimeframe(timeframe);

    if (isMobile && chartContainerRef.current) {
      chartContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const getTimeframeLabel = (timeframe) => {
    if (isMobile) {
      const labels = {
        D: 'G',
        60: '1sa',
        30: '30dk',
        1: '1dk',
      };
      return labels[timeframe] || timeframe;
    }
    return timeframe;
  };

  return (
    <div className={`tradingview-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="chart-section">
        <div className="chart-header">
          <h3>{defaultSymbol}</h3>
          <div className="chart-controls">
            {['D', '60', '30', '1'].map((timeframe) => (
              <button
                key={timeframe}
                className={`timeframe-btn ${activeTimeframe === timeframe ? 'active' : ''}`}
                onClick={() => handleTimeframeChange(timeframe)}
                aria-label={`${timeframe} zaman dilimi`}
              >
                {getTimeframeLabel(timeframe)}
              </button>
            ))}
          </div>
        </div>

        <div
          id="tradingview-chart"
          ref={chartContainerRef}
          className="chart-container"
          style={{ height: chartHeight, minHeight: chartHeight }}
        >
          {!defaultSymbol && (
            <div className="no-chart-selected">
              <p>Grafik görüntülemek için bir parite seçin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingView;
